<script>
import axios from 'axios';
import DialogManager from '../../services/DialogManager';
import HandlesErrorResponses from '../../mixins/HandlesErrorResponses';
import ModalManager from '../../services/modalManager';
import OnlinePlannerEmployees from './Employees.vue';
import { isFindable, missesRouting } from './employeeRouting';

export default {
    mixins: [HandlesErrorResponses],

    components: {
        'frm-online-planner-employees': OnlinePlannerEmployees,
    },

    props: {
        onlinePlannerInitiallyActive: {
            type: Boolean,
            default: false,
        },

        onlinePlannerInitialType: {
            type: String,
            default: null,
            validator: value => value === null || ['simple', 'advanced'].includes(value),
        },

        onlinePlannerInitialUseBodyAreas: {
            type: Boolean,
            default: false,
        },

        onlinePlannerInitialUseReservations: {
            type: Boolean,
            default: true,
        },

        onlinePlannerInitialUrl: {
            type: String,
            default: null,
        },

        onlinePlannerUpdateUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmailTemplates: {
            type: Object,
            required: true,
        },

        onlinePlannerEmailTemplateImportUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmailTemplateStatusUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmailTemplateActiveUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmployeesUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmployeesAvailabilityUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmployeeUrl: {
            type: String,
            required: true,
        },

        onlinePlannerEmployeeConfigurationUrl: {
            type: String,
            required: true,
        },
    },

    data() {
        const emailTemplateFlags = () => Object.fromEntries(Object.keys(this.onlinePlannerEmailTemplates).map(key => [key, false]));

        return {
            onlinePlannerActiveV2: this.onlinePlannerInitiallyActive,
            onlinePlannerPersistedActive: this.onlinePlannerInitiallyActive,
            onlinePlannerSettingUp: false,
            onlinePlannerConfirmationDefaulted: false,
            onlinePlannerUrlCopied: false,
            onlinePlannerEmployeesLoaded: false,
            onlinePlannerEmployees: [],
            onlinePlannerEmailTemplatesState: JSON.parse(JSON.stringify(this.onlinePlannerEmailTemplates)),
            onlinePlannerEmailTemplateImporting: emailTemplateFlags(),
            onlinePlannerEmailTemplateImports: {},
            onlinePlannerEmailTemplateToggling: emailTemplateFlags(),
            onlinePlannerStep: 1,
            onlinePlannerHighestStep: 1,
            onlinePlannerEditingFromReview: false,
            onlinePlannerType: this.onlinePlannerInitialType,
            onlinePlannerSavedType: this.onlinePlannerInitialType,
            onlinePlannerPersistedType: this.onlinePlannerInitialType,
            onlinePlannerUseBodyAreas: this.onlinePlannerInitialUseBodyAreas,
            onlinePlannerSavedUseBodyAreas: this.onlinePlannerInitialUseBodyAreas,
            onlinePlannerUseReservations: this.onlinePlannerInitialUseReservations,
            onlinePlannerSavedUseReservations: this.onlinePlannerInitialUseReservations,
            onlinePlannerReconfiguring: false,
            onlinePlannerUpdating: false,
            onlinePlannerSaveFailed: false,
            onlinePlannerSaveSucceeded: false,
            onlinePlannerSaveSuccessTimer: null,
        };
    },

    computed: {
        onlinePlannerSteps() {
            return [
                'type',
                'employees',
                ...(this.onlinePlannerType === 'advanced' ? ['body-areas'] : []),
                'emails',
                'confirmation',
                'review',
            ];
        },

        onlinePlannerStepKey() {
            return this.onlinePlannerSteps[this.onlinePlannerStep - 1];
        },

        onlinePlannerReviewStep() {
            return this.onlinePlannerSteps.length;
        },

        onlinePlannerNextStepDisabled() {
            return {
                type: !this.onlinePlannerType,
                employees: !this.onlinePlannerEmployeesLoaded,
                emails: this.onlinePlannerRequiredEmailTemplateKeys.some(key => this.onlinePlannerEmailTemplateImporting[key]),
                confirmation: this.onlinePlannerConfirmationPending,
            }[this.onlinePlannerStepKey] || false;
        },

        onlinePlannerTypeTitle() {
            return this.$t(`settings.external-interfaces.online-planner.type.${this.onlinePlannerType}.title`);
        },

        onlinePlannerTypeDescription() {
            return this.$t(`settings.external-interfaces.online-planner.type.${this.onlinePlannerType}.description`);
        },

        onlinePlannerAlternativeType() {
            return this.onlinePlannerType === 'simple' ? 'advanced' : 'simple';
        },

        onlinePlannerAlternativeTypeTitle() {
            return this.$t(`settings.external-interfaces.online-planner.type.${this.onlinePlannerAlternativeType}.title`);
        },

        onlinePlannerPatientPortalConfigurationType() {
            return this.onlinePlannerConfigurationType(this.onlinePlannerType);
        },

        onlinePlannerPersistedPatientPortalConfigurationType() {
            return this.onlinePlannerConfigurationType(this.onlinePlannerPersistedType);
        },

        onlinePlannerScheduledEmployees() {
            return this.onlinePlannerEmployees.filter(employee => employee.schedule_count > 0);
        },

        onlinePlannerAvailableEmployees() {
            return this.onlinePlannerEmployees.filter(employee => employee.online_daypart_count > 0);
        },

        onlinePlannerAvailableEmployeeNames() {
            return this.onlinePlannerAvailableEmployees.map(employee => employee.name).join(', ');
        },

        onlinePlannerRequiredEmailTemplateKeys() {
            return Object.keys(this.onlinePlannerEmailTemplatesState)
                .filter(key => this.onlinePlannerEmailTemplatesState[key].required);
        },

        onlinePlannerRequiredEmailTemplatesImported() {
            return this.onlinePlannerRequiredEmailTemplateKeys
                .every(key => this.onlinePlannerEmailTemplatesState[key].imported);
        },

        onlinePlannerListedEmailTemplateKeys() {
            return Object.keys(this.onlinePlannerEmailTemplatesState)
                .filter(key => this.onlinePlannerEmailTemplatesState[key].listed);
        },

        onlinePlannerActiveEmailTemplateKeys() {
            return this.onlinePlannerListedEmailTemplateKeys
                .filter(key => this.onlinePlannerEmailTemplatesState[key].imported && this.onlinePlannerEmailTemplatesState[key].active);
        },

        onlinePlannerActiveEmailTemplateNames() {
            return this.onlinePlannerActiveEmailTemplateKeys
                .map(key => this.$t(`settings.external-interfaces.online-planner.emails.items.${key}.title`))
                .join(', ');
        },

        onlinePlannerEmailTemplateCount() {
            return this.onlinePlannerListedEmailTemplateKeys.length;
        },

        onlinePlannerConfirmationMode() {
            return this.onlinePlannerUseReservations ? 'patient' : 'direct';
        },

        onlinePlannerConfirmationPending() {
            return this.onlinePlannerEmailTemplateImporting['make-definitive'] ||
                this.onlinePlannerEmailTemplateToggling['make-definitive'];
        },

        onlinePlannerMissingRoutingCount() {
            return this.onlinePlannerType === 'advanced'
                ? this.onlinePlannerEmployees.filter(employee => missesRouting(employee)).length
                : 0;
        },

        onlinePlannerNoFindableEmployees() {
            return this.onlinePlannerMissingRoutingCount > 0 && !this.onlinePlannerEmployees.some(employee => isFindable(employee));
        },

        onlinePlannerNoAvailableEmployees() {
            return this.onlinePlannerEmployeesLoaded && this.onlinePlannerAvailableEmployees.length === 0;
        },

        onlinePlannerSaveBlocked() {
            return this.onlinePlannerEmailWarning || this.onlinePlannerNoFindableEmployees || this.onlinePlannerNoAvailableEmployees;
        },

        onlinePlannerLegacy() {
            return this.onlinePlannerPersistedActive && !this.onlinePlannerSavedType;
        },

        onlinePlannerShowToggle() {
            return !!this.onlinePlannerSavedType || this.onlinePlannerPersistedActive || this.onlinePlannerInitiallyActive;
        },

        onlinePlannerWizardMode() {
            return this.onlinePlannerSettingUp ||
                this.onlinePlannerReconfiguring ||
                (!this.onlinePlannerSavedType && !this.onlinePlannerPersistedActive);
        },

        onlinePlannerOverviewMode() {
            return this.onlinePlannerActiveV2 && !!this.onlinePlannerSavedType && !this.onlinePlannerWizardMode;
        },

        onlinePlannerStatusOnSave() {
            if (!this.onlinePlannerPersistedActive) {
                return 'activate';
            }

            return this.onlinePlannerLegacy ? 'replace' : 'active';
        },

        onlinePlannerEmailWarning() {
            return this.onlinePlannerRequiredEmailTemplateKeys
                .some(key => !this.onlinePlannerEmailTemplatesState[key].imported || !this.onlinePlannerEmailTemplatesState[key].active);
        },

        submitButtonEnabled() {
            return !this.$store.state.core.requestPoolPending && !this.onlinePlannerUpdating;
        },

        submitButtonLoading() {
            return this.$store.state.core.requestPoolPending;
        },
    },

    watch: {
        onlinePlannerWizardMode: {
            immediate: true,
            handler(wizardMode) {
                this.$emit('configuring', wizardMode);
            },
        },

        onlinePlannerStepKey(stepKey) {
            // Prefetch the template so the confirmation step is ready
            if (stepKey === 'employees' && !this.onlinePlannerSavedType) {
                this.onlinePlannerImportEmailTemplate('make-definitive');
            }

            if (stepKey === 'confirmation') {
                this.onlinePlannerApplyDefaultConfirmation();
            }
        },

        onlinePlannerReviewStep(reviewStep) {
            this.onlinePlannerHighestStep = Math.min(this.onlinePlannerHighestStep, reviewStep);
        },
    },

    beforeDestroy() {
        window.clearTimeout(this.onlinePlannerSaveSuccessTimer);
    },

    methods: {
        onlinePlannerStepTitle(step) {
            return this.$t(`settings.external-interfaces.online-planner.steps.${this.onlinePlannerSteps[step - 1]}.title`);
        },

        onlinePlannerStepIndex(stepKey) {
            return this.onlinePlannerSteps.indexOf(stepKey) + 1;
        },

        onlinePlannerConfigurationType(type) {
            return { simple: 'Simple', advanced: 'Advanced' }[type] || 'NotConfigured';
        },

        async onlinePlannerCopyUrl() {
            await navigator.clipboard.writeText(this.onlinePlannerInitialUrl);
            this.onlinePlannerUrlCopied = true;
            window.setTimeout(() => (this.onlinePlannerUrlCopied = false), 5000);
        },

        onlinePlannerPreviousStep() {
            this.onlinePlannerStep = Math.max(1, this.onlinePlannerStep - 1);
        },

        async onlinePlannerNextStep() {
            if (this.onlinePlannerStepKey === 'emails' && this.onlinePlannerEmailWarning) {
                for (const templateKey of this.onlinePlannerRequiredEmailTemplateKeys) {
                    await this.onlinePlannerImportEmailTemplate(templateKey);

                    if (this.onlinePlannerStepKey !== 'emails') {
                        return;
                    }
                }
            }

            if (this.onlinePlannerStepKey === 'emails' && this.onlinePlannerEmailWarning) {
                return;
            }

            const nextStep = Math.min(this.onlinePlannerReviewStep, this.onlinePlannerStep + 1);

            this.onlinePlannerStep = nextStep;
            this.onlinePlannerHighestStep = Math.max(this.onlinePlannerHighestStep, nextStep);
        },

        onlinePlannerGoToStep(step) {
            if (step < 1 || step > this.onlinePlannerHighestStep) {
                return;
            }

            if (step === this.onlinePlannerReviewStep && this.onlinePlannerEmailWarning) {
                return;
            }

            if (step === this.onlinePlannerReviewStep && this.onlinePlannerEditingFromReview) {
                this.onlinePlannerReturnToReview();

                return;
            }

            this.onlinePlannerStep = step;
        },

        onlinePlannerEditStep(stepKey) {
            this.onlinePlannerEditingFromReview = true;
            this.onlinePlannerStep = this.onlinePlannerStepIndex(stepKey);
        },

        onlinePlannerReturnToReview() {
            if (this.onlinePlannerStepKey === 'emails' && this.onlinePlannerEmailWarning) {
                return;
            }

            this.onlinePlannerStep = this.onlinePlannerReviewStep;
            this.onlinePlannerHighestStep = this.onlinePlannerReviewStep;
            this.onlinePlannerEditingFromReview = false;
        },

        onlinePlannerStartWizard(stepKey, type, reconfiguring) {
            this.onlinePlannerType = type;
            this.onlinePlannerStep = this.onlinePlannerStepIndex(stepKey);
            this.onlinePlannerHighestStep = this.onlinePlannerStep;
            this.onlinePlannerEditingFromReview = false;
            this.onlinePlannerReconfiguring = reconfiguring;
        },

        onlinePlannerStartSetup() {
            this.onlinePlannerStartWizard('type', this.onlinePlannerSavedType, false);
            this.onlinePlannerSettingUp = true;
            this.onlinePlannerConfirmationDefaulted = false;
            this.onlinePlannerSaveFailed = false;
        },

        onlinePlannerShowSaveSuccess() {
            window.clearTimeout(this.onlinePlannerSaveSuccessTimer);
            this.onlinePlannerSaveSucceeded = true;
            this.onlinePlannerSaveSuccessTimer = window.setTimeout(() => this.onlinePlannerDismissSaveSuccess(), 5000);
        },

        onlinePlannerDismissSaveSuccess() {
            window.clearTimeout(this.onlinePlannerSaveSuccessTimer);
            this.onlinePlannerSaveSucceeded = false;
        },

        async onlinePlannerToggleActivation() {
            if (!this.onlinePlannerShowToggle) {
                return;
            }

            if (this.onlinePlannerActiveV2 && this.onlinePlannerSavedType && this.onlinePlannerEmailWarning) {
                this.onlinePlannerActiveV2 = false;
                return;
            }

            this.onlinePlannerDismissSaveSuccess();
            this.onlinePlannerUpdating = true;

            try {
                await this.onlinePlannerSaveConfiguration(this.onlinePlannerActiveV2);
                this.onlinePlannerPersistedActive = this.onlinePlannerActiveV2;
                this.onlinePlannerSettingUp = this.onlinePlannerSettingUp && this.onlinePlannerActiveV2;
            } catch (error) {
                this.onlinePlannerActiveV2 = !this.onlinePlannerActiveV2;
                await this.handleErrorResponse(error);
            } finally {
                this.onlinePlannerUpdating = false;
            }
        },

        async onlinePlannerSaveConfiguration(active, saveDraft = false) {
            const form = this.$el.closest('form');
            const activeInput = form.querySelector('input[name$="[online_planner_active]"]');
            const prefix = activeInput.name.slice(0, activeInput.name.indexOf(']') + 1);
            const formData = new FormData();

            // Only send this interface, so unsaved changes on other tabs stay untouched
            for (const [key, value] of new FormData(form)) {
                if (['_token', 'id'].includes(key) || key.startsWith(`${prefix}[`)) {
                    formData.append(key, value);
                }
            }

            formData.set(activeInput.name, active ? '1' : '0');

            if (saveDraft) {
                const typeInput = this.$el.querySelector('input[name$="[patient_portal_configuration_type]"]');
                const bodyAreasInput = this.$el.querySelector('input[name$="[use_body_areas]"]');
                const reservationsInput = this.$el.querySelector('input[name$="[use_reservations_for_planning]"]');

                formData.set(typeInput.name, this.onlinePlannerPatientPortalConfigurationType);
                formData.set(bodyAreasInput.name, this.onlinePlannerUseBodyAreas ? '1' : '0');
                formData.set(reservationsInput.name, this.onlinePlannerUseReservations ? '1' : '0');
            }

            formData.set('opened_tabs', 'onlineplanner');
            formData.set('_method', 'PATCH');

            await axios.post(this.onlinePlannerUpdateUrl, formData);
        },

        async onlinePlannerFinishSetup() {
            if (!this.submitButtonEnabled || !this.onlinePlannerType || this.onlinePlannerSaveBlocked) {
                return;
            }

            this.onlinePlannerUpdating = true;
            this.onlinePlannerSaveFailed = false;

            try {
                await this.onlinePlannerSaveConfiguration(true, true);
                this.onlinePlannerActiveV2 = true;
                this.onlinePlannerPersistedActive = true;
                this.onlinePlannerSettingUp = false;
                this.onlinePlannerSavedType = this.onlinePlannerType;
                this.onlinePlannerPersistedType = this.onlinePlannerType;
                this.onlinePlannerSavedUseBodyAreas = this.onlinePlannerUseBodyAreas;
                this.onlinePlannerSavedUseReservations = this.onlinePlannerUseReservations;
                this.onlinePlannerReconfiguring = false;
                this.onlinePlannerShowSaveSuccess();
            } catch (error) {
                this.onlinePlannerSaveFailed = true;
                await this.handleErrorResponse(error);
            } finally {
                this.onlinePlannerUpdating = false;
            }
        },

        async onlinePlannerToggleBodyAreas() {
            this.onlinePlannerUseBodyAreas = !this.onlinePlannerUseBodyAreas;
            this.onlinePlannerDismissSaveSuccess();
            this.onlinePlannerUpdating = true;

            try {
                await this.onlinePlannerSaveConfiguration(this.onlinePlannerPersistedActive, true);
                this.onlinePlannerSavedUseBodyAreas = this.onlinePlannerUseBodyAreas;
            } catch (error) {
                this.onlinePlannerUseBodyAreas = this.onlinePlannerSavedUseBodyAreas;
                await this.handleErrorResponse(error);
            } finally {
                this.onlinePlannerUpdating = false;
            }
        },

        async onlinePlannerConfirmTypeSwitch() {
            const fromOverview = this.onlinePlannerOverviewMode;
            const alternativeType = this.onlinePlannerAlternativeType;

            try {
                await DialogManager.confirm(
                    `<div class="online-planner-type-switch-confirmation">
                        <h3>${this.$t('settings.external-interfaces.online-planner.review.switch-type-title', {
                            type: this.onlinePlannerAlternativeTypeTitle,
                        })}</h3>
                        <p>${this.$t(`settings.external-interfaces.online-planner.review.switch-type-consequence.${alternativeType}`)}</p>
                        <p class="online-planner-type-switch-confirmation__preserved">${this.$t(`settings.external-interfaces.online-planner.review.switch-type-preserved.${alternativeType}`)}</p>
                    </div>`,
                    {
                        ok: this.$t('settings.external-interfaces.online-planner.review.switch-type-confirm'),
                    }
                );
            } catch (error) {
                return;
            }

            if (fromOverview) {
                this.onlinePlannerSaveFailed = false;
            }

            this.onlinePlannerStartWizard('employees', alternativeType, fromOverview);
            this.onlinePlannerDismissSaveSuccess();
        },

        async onlinePlannerRestartSetup() {
            try {
                await DialogManager.confirm(
                    `<div class="online-planner-restart-confirmation">
                        <h3>${this.$t('settings.external-interfaces.online-planner.overview.restart-confirmation-title')}</h3>
                        <p>${this.$t('settings.external-interfaces.online-planner.overview.restart-confirmation-description')}</p>
                    </div>`,
                    {
                        ok: this.$t('settings.external-interfaces.online-planner.overview.restart-confirm'),
                    }
                );
            } catch (error) {
                return;
            }

            this.onlinePlannerStartWizard('type', this.onlinePlannerSavedType, true);
            this.onlinePlannerUseBodyAreas = this.onlinePlannerSavedUseBodyAreas;
            this.onlinePlannerUseReservations = this.onlinePlannerSavedUseReservations;
            this.onlinePlannerSaveFailed = false;
            this.onlinePlannerDismissSaveSuccess();
        },

        async onlinePlannerImportEmailTemplate(templateKey) {
            const template = this.onlinePlannerEmailTemplatesState[templateKey];

            // Importing an inactive required template activates it
            if (!template || (template.imported && (template.active || !template.required))) {
                return;
            }

            // Join a running import instead of starting a second one
            if (!this.onlinePlannerEmailTemplateImports[templateKey]) {
                this.onlinePlannerEmailTemplateImports[templateKey] = this.onlinePlannerRequestEmailTemplateImport(templateKey)
                    .finally(() => delete this.onlinePlannerEmailTemplateImports[templateKey]);
            }

            await this.onlinePlannerEmailTemplateImports[templateKey];
        },

        async onlinePlannerRequestEmailTemplateImport(templateKey) {
            const template = this.onlinePlannerEmailTemplatesState[templateKey];

            this.onlinePlannerEmailTemplateImporting[templateKey] = true;

            try {
                const url = this.onlinePlannerEmailTemplateImportUrl.replace('_template_key_', templateKey);

                this.onlinePlannerEmailTemplatesState[templateKey] = {
                    ...template,
                    ...(await axios.post(url)).data.data,
                };
            } catch (err) {
                await this.handleErrorResponse(err);
            } finally {
                this.onlinePlannerEmailTemplateImporting[templateKey] = false;
            }
        },

        async onlinePlannerEditEmailTemplate(templateKey) {
            const template = this.onlinePlannerEmailTemplatesState[templateKey];
            const saved = await ModalManager.openModal(template.edit_url, undefined, undefined, 'full');

            if (!saved) {
                return;
            }

            try {
                const status = (await axios.post(
                    this.onlinePlannerEmailTemplateStatusUrl.replace('_template_key_', templateKey)
                )).data.data;

                this.onlinePlannerEmailTemplatesState[templateKey] = {
                    ...template,
                    ...status,
                };
            } catch (error) {
                await this.handleErrorResponse(error);
            }
        },

        async onlinePlannerSelectConfirmationMode(mode) {
            if (mode === this.onlinePlannerConfirmationMode || this.onlinePlannerConfirmationPending) {
                return;
            }

            if (mode === 'patient') {
                await this.onlinePlannerImportEmailTemplate('make-definitive');
            }

            const template = this.onlinePlannerEmailTemplatesState['make-definitive'];

            if (mode === 'patient' && !template.imported) {
                return;
            }

            if (template.imported && template.active !== (mode === 'patient')) {
                await this.onlinePlannerToggleEmailTemplate('make-definitive');
            }

            this.onlinePlannerUseReservations = mode === 'patient';
        },

        async onlinePlannerApplyDefaultConfirmation() {
            if (this.onlinePlannerSavedType || this.onlinePlannerConfirmationDefaulted) {
                return;
            }

            // New setups default to patient confirmation by mail
            this.onlinePlannerConfirmationDefaulted = true;
            await this.onlinePlannerImportEmailTemplate('make-definitive');

            if (!this.onlinePlannerEmailTemplatesState['make-definitive'].imported) {
                this.onlinePlannerUseReservations = false;
                return;
            }

            if (!this.onlinePlannerEmailTemplatesState['make-definitive'].active) {
                await this.onlinePlannerToggleEmailTemplate('make-definitive');
            }

            this.onlinePlannerUseReservations = this.onlinePlannerEmailTemplatesState['make-definitive'].active;
        },

        async onlinePlannerToggleEmailTemplate(templateKey) {
            const template = this.onlinePlannerEmailTemplatesState[templateKey];
            const enabled = !template.active;

            template.active = enabled;
            this.onlinePlannerEmailTemplateToggling[templateKey] = true;

            try {
                const url = this.onlinePlannerEmailTemplateActiveUrl.replace('_template_key_', templateKey);

                this.onlinePlannerEmailTemplatesState[templateKey] = {
                    ...template,
                    ...(await axios.post(url, { active: enabled })).data.data,
                };
            } catch (err) {
                template.active = !enabled;
                await this.handleErrorResponse(err);
            } finally {
                this.onlinePlannerEmailTemplateToggling[templateKey] = false;
            }
        },
    },
};
</script>
